import React, { memo, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Stack,
  LoadingButton,
  Icon,
  Divider,
  Card,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Persona
} from 'investira.react.components';
import { PageTemplate } from '../../../components/templates';
import { useAvaliacoesContext } from '../../../contexts/AvaliacoesContext';
import AvalicoesSaveStatus from '../_components/AvalicoesSaveStatus';
import SessaoNavProgress from '../_components/SessaoNavProgress';
import AvaliacoesGestorEditQuestoes from './AvaliacoesGestorEditQuestoes';
import AvaliacoesProgresso from '../_components/AvaliacoesProgresso';
import { PessoaImage } from '../../../components/molecules';
import { AVALIACAO_QUESTOES, AVALIACAO_SESSOES_QUESTOES } from '../../../enums';

const AvaliacoesGestorEdit = memo(props => {
  const { state, actions } = useAvaliacoesContext();
  const { sessoes, gestor, activeCategoryId, activeQuestionId } = state;
  const [anexos, setAnexos] = useState([]);
  const [respostasGestor, setRespostasGestor] = useState({});
  const [assinaturaDialogOpen, setAssinaturaDialogOpen] = useState(false);
  const [assinaturaConfirmada, setAssinaturaConfirmada] = useState(false);
  const [entregaFinalizada, setEntregaFinalizada] = useState(false);
  const [mostrarApenasNaoRespondidas, setMostrarApenasNaoRespondidas] = useState(false);
  const xInputAnexosRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  const handleSelectCategoria = (_pEvent, pSessao) => {
    pSessao.id && actions.handleChangeActiveSessaoId(pSessao.id);
    const xFirstQuestionId = AVALIACAO_SESSOES_QUESTOES[pSessao.id]?.questoes?.[0]?.id;
    xFirstQuestionId && actions.handleChangeActiveQuestionId(xFirstQuestionId);
  };

  const handleOpenAnexos = () => xInputAnexosRef.current?.click();

  const handleAddAnexos = pEvent => {
    const xFiles = Array.from(pEvent.target.files || []);
    if (!xFiles.length) return;
    setAnexos(pPrev => [...pPrev, ...xFiles]);
    pEvent.target.value = '';
  };

  const handleRemoveAnexo = pIndex =>
    setAnexos(pPrev => pPrev.filter((_pFile, pI) => pI !== pIndex));

  const handleRemoveAnexoQuestao = (pQuestionId, pIndex) => {
    setRespostasGestor(pPrev => {
      const xRespostaAtual = pPrev?.[pQuestionId] || {};
      const xAnexosAtualizados = (xRespostaAtual.anexos || []).filter(
        (_pFile, pI) => pI !== pIndex
      );

      return {
        ...pPrev,
        [pQuestionId]: {
          ...xRespostaAtual,
          anexos: xAnexosAtualizados
        }
      };
    });
  };

  const formatFileSize = pBytes => {
    if (!pBytes) return '0 B';
    const xUnits = ['B', 'KB', 'MB', 'GB'];
    const xUnitIndex = Math.min(Math.floor(Math.log(pBytes) / Math.log(1024)), xUnits.length - 1);
    const xValue = pBytes / 1024 ** xUnitIndex;
    return `${xValue.toFixed(xUnitIndex === 0 ? 0 : 1)} ${xUnits[xUnitIndex]}`;
  };

  const xQuestoes = Object.values(AVALIACAO_QUESTOES);

  const xRespondidas = useMemo(() => {
    return xQuestoes.reduce((pCount, pQuestao) => {
      return respostasGestor?.[pQuestao.id]?.resposta_id ? pCount + 1 : pCount;
    }, 0);
  }, [xQuestoes, respostasGestor]);

  const xSessoesProgress = useMemo(() => {
    return Object.fromEntries(
      Object.values(sessoes).map(xSessao => {
        const xQuestoesSessao = AVALIACAO_SESSOES_QUESTOES[xSessao.id]?.questoes || [];
        const xTotalSessao = xQuestoesSessao.length;
        const xRespondidasSessao = xQuestoesSessao.reduce((pCount, pQuestao) => {
          return respostasGestor?.[pQuestao.id]?.resposta_id ? pCount + 1 : pCount;
        }, 0);
        const xProgression = xTotalSessao ? (xRespondidasSessao / xTotalSessao) * 100 : 0;

        return [
          xSessao.id,
          {
            ...xSessao,
            progression: xProgression,
            completed: xTotalSessao > 0 && xRespondidasSessao >= xTotalSessao
          }
        ];
      })
    );
  }, [sessoes, respostasGestor]);

  const xAnexosQuestoes = useMemo(() => {
    return Object.entries(respostasGestor || {}).flatMap(([pQuestionId, pResposta]) => {
      const xAnexosResposta = pResposta?.anexos || [];
      return xAnexosResposta.map((pFile, pIndex) => ({
        file: pFile,
        questionId: pQuestionId,
        index: pIndex
      }));
    });
  }, [respostasGestor]);

  const xTotalAnexos = anexos.length + xAnexosQuestoes.length;

  const handleQuestionChange = (pQuestionId, pData = {}) => {
    setRespostasGestor(pPrev => ({
      ...pPrev,
      [pQuestionId]: {
        ...(pPrev?.[pQuestionId] || {}),
        ...pData
      }
    }));
  };

  const xTodasRespondidas = xQuestoes.length > 0 && xRespondidas >= xQuestoes.length;

  const handleOpenAssinaturaDialog = () => {
    // if (!xTodasRespondidas) return;
    setAssinaturaDialogOpen(true);
  };

  const handleCloseAssinaturaDialog = () => {
    setAssinaturaDialogOpen(false);
    setAssinaturaConfirmada(false);
  };

  const handleFinalizarEntrega = () => {
    if (!assinaturaConfirmada) return;
    setEntregaFinalizada(true);
    setAssinaturaDialogOpen(false);
  };

  if (entregaFinalizada) {
    return (
      <PageTemplate
        title={gestor.pessoa_fantasia || 'EXEMPLO'}
        subtitle={'Avaliação'}
        maxWidth="md"
        breadcrumbs={false}
        footerProps={{
          variant: 'avaliacao'
        }}>
        <Card sx={{ p: 4 }}>
          <Stack gap={1.5}>
            <Typography variant="h5" color="textPrimary">
              Obrigado pelo envio.
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Sua avaliação foi entregue com sucesso.
            </Typography>
            <Typography variant="body2" color="textSecondary">
              O formulário foi encerrado e não pode mais ser alterado.
            </Typography>
          </Stack>
        </Card>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate
      title={gestor.pessoa_fantasia || 'EXEMPLO'}
      subtitle={'Avaliação'}
      maxWidth="xl"
      breadcrumbs={false}
      headerRenderActions={
        <Stack direction="row" spacing={2} alignItems="center">
          <LoadingButton
            variant="outlined"
            color="primary"
            startIcon={<Icon iconName="upload" size={16} />} // Trocar o icone enquanto estiver atualizando passivamente
            size="small"
            sx={{ px: 2 }}
            type="submit"
            disabled={props.disabledButtons}
            //loading={isSubmitting}
          >
            {/* {isSubmitting ? "Salvando..." : "Salvar Rascunho"} */}
            Salvar Rascunho
          </LoadingButton>
          <LoadingButton
            variant="contained"
            color="primary"
            startIcon={<Icon iconName="paper_plane" size={16} />} // Trocar o icone enquanto estiver atualizando passivamente
            size="small"
            sx={{ px: 2 }}
            onClick={handleOpenAssinaturaDialog}
            //disabled={!xTodasRespondidas || props.disabledButtons}
            //loading={isSubmitting}
          >
            {/* {isSubmitting ? "Salvando..." : "Salvar Rascunho"} */}
            Entregar
          </LoadingButton>
        </Stack>
      }
      footerProps={{
        variant: 'avaliacao'
      }}>
      <Stack direction="row" gap={6}>
        <Stack
          id="sessoes"
          direction="column"
          gap={2}
          sx={{
            position: 'sticky',
            top: 24,
            alignSelf: 'flex-start',
            maxHeight: 'calc(100vh - 24px)',
            overflowY: 'auto',
            minWidth: '340px',
            maxWidth: '340px',
            width: '100%'
          }}>
          <AvaliacoesProgresso
            variant="gestor"
            qtdQuestoes={xQuestoes.length}
            respondidas={xRespondidas}
          />
          <Divider />
          <SessaoNavProgress
            sessoes={xSessoesProgress}
            activeCategoryId={activeCategoryId}
            onClick={handleSelectCategoria}
          />
          <Divider />
          <Stack gap={2}>
            <Stack>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ textTransform: 'uppercase', fontWeight: 500 }}>
                Solicitante
              </Typography>
            </Stack>
            <Stack direction="row" gap={2} alignItems="center">
              <Persona alt={' Augusto Souza'} />

              <Stack>
                <Typography variant="body1" color="textPrimary" sx={{ fontWeight: 500 }}>
                  Augusto Souza
                </Typography>
                <Typography variant="caption" color="textPrimary">
                  Analista de Fundos
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Stack>

        <Stack direction="column" flexGrow="1" gap={2}>
          <Stack direction="row" justifyContent="space-between">
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={mostrarApenasNaoRespondidas}
                  onChange={pEvent => setMostrarApenasNaoRespondidas(pEvent.target.checked)}
                />
              }
              label={
                <Typography variant="caption" color="textPrimary" sx={{ fontWeight: 500 }}>
                  Apenas não respondidas
                </Typography>
              }
            />
            <AvalicoesSaveStatus status={0} />
          </Stack>
          <Divider />
          <AvaliacoesGestorEditQuestoes
            sessoes={sessoes}
            activeCategoryId={activeCategoryId}
            activeQuestionId={activeQuestionId}
            onSelect={actions.handleChangeActiveQuestionId}
            respostas={respostasGestor}
            onQuestionChange={handleQuestionChange}
            mostrarApenasNaoRespondidas={mostrarApenasNaoRespondidas}
          />
        </Stack>

        <Stack
          id="anexos"
          direction="column"
          gap={2}
          sx={{
            position: 'sticky',
            top: 24,
            alignSelf: 'flex-start',
            maxHeight: 'calc(100vh - 24px)',
            overflowY: 'auto',
            minWidth: '340px',
            maxWidth: '340px',
            width: '100%'
          }}>
          <Stack gap={1.5}>
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ textTransform: 'uppercase', fontWeight: 500 }}>
              Anexos
            </Typography>
            <Card
              onClick={handleOpenAnexos}
              sx={{
                p: 2,
                border: '1px dashed',
                borderColor: 'divider',
                cursor: 'pointer',
                '&:hover': { borderColor: 'primary.main', backgroundColor: 'action.hover' }
              }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Icon iconName="upload" size={18} />
                  <Typography variant="body2">
                    Clique para anexar arquivos {xTotalAnexos ? `(${xTotalAnexos})` : ''}
                  </Typography>
                </Stack>
                <LoadingButton
                  size="small"
                  variant="outlined"
                  onClick={handleOpenAnexos}
                  sx={{ px: 2 }}>
                  Selecionar
                </LoadingButton>
              </Stack>
            </Card>
            <input ref={xInputAnexosRef} type="file" multiple hidden onChange={handleAddAnexos} />
            {(anexos.length > 0 || xAnexosQuestoes.length > 0) && (
              <Stack gap={1}>
                {anexos.map((pFile, pIndex) => (
                  <Card key={`${pFile.name}-${pFile.size}-${pIndex}`} sx={{ p: 1.25 }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={1}>
                      <Stack minWidth={0}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                          {pFile.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {formatFileSize(pFile.size)}
                        </Typography>
                      </Stack>
                      <LoadingButton
                        size="small"
                        color="error"
                        variant="text"
                        onClick={() => handleRemoveAnexo(pIndex)}>
                        Remover
                      </LoadingButton>
                    </Stack>
                  </Card>
                ))}
                {xAnexosQuestoes.map(({ file, questionId, index }) => (
                  <Card
                    key={`${file.name}-${file.size}-${file.lastModified}-${questionId}-${index}`}
                    sx={{ p: 1.25 }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={1}>
                      <Stack minWidth={0}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                          {file.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Questão {questionId} • {formatFileSize(file.size)}
                        </Typography>
                      </Stack>
                      <LoadingButton
                        size="small"
                        color="error"
                        variant="text"
                        onClick={() => handleRemoveAnexoQuestao(questionId, index)}>
                        Remover
                      </LoadingButton>
                    </Stack>
                  </Card>
                ))}
              </Stack>
            )}
          </Stack>
          <Divider />
          <LoadingButton
            variant="contained"
            color="primary"
            startIcon={<Icon iconName="paper_plane" size={16} />} // Trocar o icone enquanto estiver atualizando passivamente
            size="small"
            sx={{ px: 2 }}
            onClick={handleOpenAssinaturaDialog}
            disabled={!xTodasRespondidas || props.disabledButtons}
            //loading={isSubmitting}
          >
            {/* {isSubmitting ? "Salvando..." : "Salvar Rascunho"} */}
            Entregar
          </LoadingButton>
        </Stack>
      </Stack>
      <Dialog
        open={assinaturaDialogOpen}
        onClose={handleCloseAssinaturaDialog}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>Assinatura de confirmação</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            Ao entregar, você atesta que as respostas enviadas refletem dados verdadeiros e estão
            alinhadas com a realidade da instituição.
          </DialogContentText>
          <DialogContentText sx={{ mb: 2 }}>
            <b>
              Atenção: após entregar, não será mais possível editar o questionário. Certifique-se de
              que todas as respostas estejam corretas antes de prosseguir.
            </b>
          </DialogContentText>
          <FormControlLabel
            control={
              <Checkbox
                checked={assinaturaConfirmada}
                onChange={pEvent => setAssinaturaConfirmada(pEvent.target.checked)}
              />
            }
            label="Confirmo a veracidade das informações prestadas."
          />
        </DialogContent>
        <DialogActions>
          <LoadingButton variant="text" onClick={handleCloseAssinaturaDialog}>
            Cancelar
          </LoadingButton>
          <LoadingButton
            variant="contained"
            onClick={handleFinalizarEntrega}
            disabled={!assinaturaConfirmada}>
            Entregar
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </PageTemplate>
  );
});

AvaliacoesGestorEdit.displayName = 'AvaliacoesGestorEdit';

export default AvaliacoesGestorEdit;
